/**
 * TA7 Leasehold Information Form — Disclosure Questions
 *
 * Only shown when tenure === 'leasehold'.
 * Based on the Law Society TA7 form structure.
 */

export type TA7AnswerType = 'yesNoNotKnown' | 'text' | 'yesNo';

export interface TA7Question {
  id: string;
  section: number;
  sectionTitle: string;
  questionText: string;
  helpText?: string;
  answerType: TA7AnswerType;
  conditionalOn?: { questionId: string; answer: string };
}

export interface TA7Section {
  section: number;
  title: string;
  questions: TA7Question[];
}

const ta7Questions: TA7Question[] = [
  // ── Section 1: Lease details ───────────────────────────────────────
  {
    id: 'ta7-1.1',
    section: 1,
    sectionTitle: 'Lease details',
    questionText: 'What is the remaining term of the lease?',
    helpText: 'Check your lease for the original term and start date.',
    answerType: 'text',
  },
  {
    id: 'ta7-1.1a',
    section: 1,
    sectionTitle: 'Lease details',
    questionText: 'Is the remaining lease term less than 80 years?',
    helpText: 'Leases with fewer than 80 years remaining can be difficult to mortgage and may significantly affect the property value. Most lenders require at least 70–80 years remaining at the end of the mortgage term.',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta7-1.2',
    section: 1,
    sectionTitle: 'Lease details',
    questionText: 'What is the current annual ground rent?',
    answerType: 'text',
  },
  {
    id: 'ta7-1.3',
    section: 1,
    sectionTitle: 'Lease details',
    questionText: 'Does the ground rent increase? If so, how and when?',
    answerType: 'text',
  },
  {
    id: 'ta7-1.3a',
    section: 1,
    sectionTitle: 'Lease details',
    questionText: 'Does the ground rent contain a doubling clause or escalation linked to RPI/CPI?',
    helpText: 'Some leases contain ground rent clauses that double at fixed intervals or increase with inflation. These can affect mortgageability and are important for buyers to know about.',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta7-1.4',
    section: 1,
    sectionTitle: 'Lease details',
    questionText: 'What is the current annual service charge?',
    answerType: 'text',
  },
  {
    id: 'ta7-1.5',
    section: 1,
    sectionTitle: 'Lease details',
    questionText: 'Are there any arrears of ground rent or service charge?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta7-1.5a',
    section: 1,
    sectionTitle: 'Lease details',
    questionText: 'Please give details of the arrears.',
    answerType: 'text',
    conditionalOn: { questionId: 'ta7-1.5', answer: 'yes' },
  },

  // ── Section 2: Management company ──────────────────────────────────
  {
    id: 'ta7-2.1',
    section: 2,
    sectionTitle: 'Management company',
    questionText: 'Who manages the building or estate?',
    helpText: 'Provide the name and contact details of the managing agent or management company.',
    answerType: 'text',
  },
  {
    id: 'ta7-2.1a',
    section: 2,
    sectionTitle: 'Management company',
    questionText: 'What are the name and contact details of the freeholder?',
    helpText: 'The freeholder (landlord) is the person or company that owns the freehold of the building. This may be different from the managing agent.',
    answerType: 'text',
  },
  {
    id: 'ta7-2.2',
    section: 2,
    sectionTitle: 'Management company',
    questionText: 'Do you own a share in the freehold or management company?',
    answerType: 'yesNoNotKnown',
  },

  // ── Section 3: Alterations and consents ────────────────────────────
  {
    id: 'ta7-3.1',
    section: 3,
    sectionTitle: 'Alterations and consents',
    questionText: 'Have you made any alterations that required the landlord\'s consent?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta7-3.1a',
    section: 3,
    sectionTitle: 'Alterations and consents',
    questionText: 'Was consent obtained? Please provide details.',
    answerType: 'text',
    conditionalOn: { questionId: 'ta7-3.1', answer: 'yes' },
  },

  // ── Section 4: Complaints and disputes ─────────────────────────────
  {
    id: 'ta7-4.1',
    section: 4,
    sectionTitle: 'Complaints and disputes',
    questionText: 'Are there any disputes with the landlord, management company, or other leaseholders?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta7-4.1a',
    section: 4,
    sectionTitle: 'Complaints and disputes',
    questionText: 'Please give details of the dispute.',
    answerType: 'text',
    conditionalOn: { questionId: 'ta7-4.1', answer: 'yes' },
  },
  {
    id: 'ta7-4.2',
    section: 4,
    sectionTitle: 'Complaints and disputes',
    questionText: 'Have there been any disputes about the service charge or its reasonableness?',
    helpText: 'This includes any applications to the First-tier Tribunal (Property Chamber) or informal disputes about service charge amounts or items.',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta7-4.2a',
    section: 4,
    sectionTitle: 'Complaints and disputes',
    questionText: 'Please give details of the service charge dispute.',
    answerType: 'text',
    conditionalOn: { questionId: 'ta7-4.2', answer: 'yes' },
  },

  // ── Section 5: Insurance ───────────────────────────────────────────
  {
    id: 'ta7-5.1',
    section: 5,
    sectionTitle: 'Insurance',
    questionText: 'Is the building insured by the landlord or management company?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta7-5.2',
    section: 5,
    sectionTitle: 'Insurance',
    questionText: 'Have there been any insurance claims on the building in the last 5 years?',
    answerType: 'yesNoNotKnown',
  },

  // ── Section 6: Major works and service charge ──────────────────────
  {
    id: 'ta7-6.1',
    section: 6,
    sectionTitle: 'Major works and service charge',
    questionText: 'Are you aware of any planned major works to the building?',
    helpText: 'For example, roof repairs, external decoration, lift replacement.',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta7-6.1a',
    section: 6,
    sectionTitle: 'Major works and service charge',
    questionText: 'Please give details of the planned works and estimated cost.',
    answerType: 'text',
    conditionalOn: { questionId: 'ta7-6.1', answer: 'yes' },
  },
  {
    id: 'ta7-6.2',
    section: 6,
    sectionTitle: 'Major works and service charge',
    questionText: 'Have you received a Section 20 notice for major works?',
    helpText: 'A Section 20 notice is a legal requirement before a landlord can carry out qualifying works costing more than £250 per leaseholder.',
    answerType: 'yesNoNotKnown',
  },

  // ── Section 7: Lease restrictions ──────────────────────────────────
  {
    id: 'ta7-7.1',
    section: 7,
    sectionTitle: 'Lease restrictions',
    questionText: 'Does the lease contain any restrictions on keeping pets?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta7-7.2',
    section: 7,
    sectionTitle: 'Lease restrictions',
    questionText: 'Does the lease contain any restrictions on subletting or short-term letting (e.g. Airbnb)?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta7-7.3',
    section: 7,
    sectionTitle: 'Lease restrictions',
    questionText: 'Does the lease contain any restrictions on running a business from the property?',
    answerType: 'yesNoNotKnown',
  },
  {
    id: 'ta7-7.4',
    section: 7,
    sectionTitle: 'Lease restrictions',
    questionText: 'Are there any other significant restrictions in the lease you are aware of?',
    helpText: 'For example, restrictions on flooring type, satellite dishes, window alterations, or use of communal areas.',
    answerType: 'text',
  },
];

export function getTA7Sections(): TA7Section[] {
  const sectionMap = new Map<number, TA7Section>();
  for (const q of ta7Questions) {
    if (!sectionMap.has(q.section)) {
      sectionMap.set(q.section, { section: q.section, title: q.sectionTitle, questions: [] });
    }
    sectionMap.get(q.section)!.questions.push(q);
  }
  return Array.from(sectionMap.values()).sort((a, b) => a.section - b.section);
}

export function getAllTA7Questions(): TA7Question[] {
  return ta7Questions;
}

export function countVisibleTA7Questions(answers: Record<string, string>): number {
  return ta7Questions.filter(q => {
    if (!q.conditionalOn) return true;
    return answers[q.conditionalOn.questionId] === q.conditionalOn.answer;
  }).length;
}

export function countAnsweredTA7Questions(answers: Record<string, string>): number {
  return ta7Questions.filter(q => {
    if (!q.conditionalOn) return !!answers[q.id];
    if (answers[q.conditionalOn.questionId] !== q.conditionalOn.answer) return false;
    return !!answers[q.id];
  }).length;
}
